"use client";

import { useEffect, useLayoutEffect } from "react";
import type { ScrollToBottom, StickToBottomContext } from "use-stick-to-bottom";

type ConversationPositionReporterProps = {
  initialPositionKey: string | null;
  isAtBottom: boolean;
  onInitialPositioned: (positionKey: string) => void;
  onChange: (isAtBottom: boolean) => void;
  onViewportChange: () => void;
  scrollRequestKey: string | null;
  scrollToBottom: ScrollToBottom;
  scrollRef: StickToBottomContext["scrollRef"];
};

/**
 * 会話位置を入力欄へ共有し、新しい送信時だけ滑らかに応答領域を確保します。
 */
export function ConversationPositionReporter({
  initialPositionKey,
  isAtBottom,
  onInitialPositioned,
  onChange,
  onViewportChange,
  scrollRequestKey,
  scrollToBottom,
  scrollRef,
}: ConversationPositionReporterProps) {
  useLayoutEffect(() => {
    if (!initialPositionKey) return;

    let cancelled = false;
    const initialPosition = scrollToBottom({
      animation: "instant",
      ignoreEscapes: true,
    });
    void Promise.resolve(initialPosition).then(() => {
      if (!cancelled) onInitialPositioned(initialPositionKey);
    });

    return () => {
      cancelled = true;
    };
  }, [initialPositionKey, onInitialPositioned, scrollToBottom]);

  useEffect(() => {
    onChange(isAtBottom);
  }, [isAtBottom, onChange]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    let frame = 0;
    const scheduleViewportReport = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(onViewportChange);
    };

    scheduleViewportReport();
    scrollElement.addEventListener("scroll", scheduleViewportReport, {
      passive: true,
    });
    window.addEventListener("resize", scheduleViewportReport);

    return () => {
      window.cancelAnimationFrame(frame);
      scrollElement.removeEventListener("scroll", scheduleViewportReport);
      window.removeEventListener("resize", scheduleViewportReport);
    };
  }, [onViewportChange, scrollRef]);

  useEffect(() => {
    if (!scrollRequestKey) return;
    void scrollToBottom({
      animation: "smooth",
      ignoreEscapes: true,
    });
  }, [scrollRequestKey, scrollToBottom]);

  return null;
}
